import React from 'react';
import {
  Button,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Icon,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { colors } from '../../utils';
import { V4_BUTTON_DEFAULT, V4_ICON_BUTTON } from '../../theme/v4Compat';
import { extractApiErrorMessage, notifications } from '../../utils';
import { SettingsService } from '../../hooks/SettingsService';
import { Title, Loader, FarmContactCard } from '../../elements';
import useAuthContext from '../../hooks/useAuthContext';

import { LoaderSize } from '../../constants/constants';

import { FarmContactViewModel } from '../../models/user';
import Alert from '@mui/material/Alert';
import { FarmDoctorAddModal as FarmContactAddModal } from '../../components';
import { useTranslation } from 'react-i18next';
import UserRole from '../../constants/user-role';

const FarmStaff: React.FC<any> = () => {
  const styles = sxStyles(useTheme());
  const settingsService = new SettingsService();
  const { state } = useAuthContext();
  const { t } = useTranslation();

  const [isBusy, setIsBusy] = React.useState(false);
  const [isBusyUpdating, setIsBusyUpdating] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const [addContactOpened, setAddContactOpened] = React.useState(false);
  const [contacts, setContacts] = React.useState([] as FarmContactViewModel[]);

  const loadFarmSettings = async () => {
    setIsBusy(true);

    settingsService
      .getFarmSettings()
      .then((settings) => {
        setContacts(settings.contacts);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (loaded === false) {
      const loadData = async () => loadFarmSettings();

      loadData();
    }
  }, [loaded]);

  const updateFarmSettings = () => {
    if (!validated()) {
      return;
    }

    setIsBusyUpdating(true);

    settingsService
      .updateFarmSettings(undefined, contacts)
      .then(() => {
        notifications.success(t('f-settings-upd-ok'));
        setLoaded(false);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusyUpdating(false);
      });
  };

  const validated = (): boolean => {
    if (contacts.length <= 0) {
      notifications.warning(t('f-val-farm-contact'));
      return false;
    }

    return true;
  };

  const handleRemoveContact = (index: number) => {
    contacts.splice(index, 1);
    setContacts([...contacts]);
  };

  const handleMakePrimary = (index: number) => {
    contacts.forEach((x) => (x.isPrimary = false));
    contacts[index].isPrimary = true;
    setContacts([...contacts]);
  };

  const handleAddContact = (contact: FarmContactViewModel | null) => {
    if (contact === null) {
      setAddContactOpened(false);
      return;
    }

    setContacts([...contacts, contact]);
    setAddContactOpened(false);
  };

  const renderContactCardActionSection = (contact: FarmContactViewModel, index: number) => {
    return (
      <>
        {!contact.isPrimary && (
          <Tooltip arrow title={t('f-settings-upd-con-prim')}>
            <IconButton color="primary" onClick={() => handleMakePrimary(index)} sx={V4_ICON_BUTTON}>
              <Icon>{'star_outline'}</Icon>
            </IconButton>
          </Tooltip>
        )}
        <Tooltip arrow title={t('f-settings-upd-con-del')}>
          <IconButton color="primary" onClick={() => handleRemoveContact(index)} sx={V4_ICON_BUTTON}>
            <Icon>{'delete_outline'}</Icon>
          </IconButton>
        </Tooltip>
      </>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item container xs={12} spacing={1}>
        <Grid item>
          <Icon color="primary" fontSize="large">
            {'people_outline'}
          </Icon>
        </Grid>
        <Grid item>
          <Title>{t('f-farm-staff')}</Title>
        </Grid>
      </Grid>

      <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

      {loaded && (
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <>
                <CardHeader avatar={<Icon color="primary">{'contact_page'}</Icon>} title={t('f-settings-farm-con')} />

                <CardContent>
                  <Grid container spacing={1}>
                    {contacts &&
                      contacts.map((contact, index) => (
                        <Grid item xs={12} md={6} key={`con-${index}`}>
                          <FarmContactCard contact={contact} action={renderContactCardActionSection(contact, index)} />
                        </Grid>
                      ))}
                  </Grid>
                  {!contacts.length && <Alert severity="warning">{t('f-settings-upd-con-empty')}</Alert>}
                </CardContent>

                <CardActions sx={styles.cardFooter}>
                  {isBusyUpdating && <CircularProgress />}

                  <Button
                    size="small"
                    disabled={state.role === UserRole.user || isBusyUpdating}
                    color="inherit"
                    startIcon={<Icon>{'add_circle_outline'}</Icon>}
                    onClick={() => setAddContactOpened(true)}
                    variant="contained"
                    sx={V4_BUTTON_DEFAULT}
                  >
                    {t('f-settings-upd-con-add')}
                  </Button>

                  <Button
                    size="small"
                    disabled={state.role === UserRole.user || isBusyUpdating}
                    color="primary"
                    startIcon={<Icon>{'save'}</Icon>}
                    onClick={updateFarmSettings}
                    variant="contained"
                  >
                    {t('f-com-save')}
                  </Button>
                </CardActions>
              </>
            </Grid>
          </Grid>
        </Grid>
      )}

      <FarmContactAddModal opened={addContactOpened} onModalAction={handleAddContact} />
    </Grid>
  );
};

export default FarmStaff;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (_theme: Theme) =>
  ({
    tableHeader: {
      background: colors.defaultGrey
    },
    cardHeader: {
      paddingBottom: 0,
      paddingTop: '5px',
      paddingLeft: '5px',
      background: colors.defaultGrey
    },
    cardFooter: {
      justifyContent: 'flex-end'
    }
  }) as const;
